import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Download, X, Info, RotateCcw, ChevronUp, ChevronDown } from 'lucide-react';
import TagCostInfoModal from '../../components/common/TagCostInfoModal';
import CommonTable from '../../Common/CommonTable';
import { getVehicleClassData } from '../../Utils/helper';
import { vehicleClasses } from '../../Utils/Constants';
import moment from 'moment';
import { Eye, EyeOff } from 'lucide-react';
import { ACTIONS } from '../../redux/actions';
import TextInput from '../../Commponent/TextInput';
import CustomSelectInput from '../../Commponent/SelectInput';
import { API } from '../../Common/Api';
import { ErrorToast, SuccessToast } from '../../Utils/toast';
import uploading from '../../assets/uploading.png';
import download from '../../assets/downloading.png';
import ReverseInventoryModal from '../../Commponent/ReverseInventoryModal';

const MasterFASTag = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // The back arrow is only meaningful when this page was opened as a drill-in
  // (e.g. from the inventory "Master FASTag" button). When reached from the
  // Settings menu it is a top-level destination, so no back button is shown.
  const showBackButton = !!location.state?.fromInventory;
  const dispatch = useDispatch();
  const GetDistributorNames = useSelector((state) => {
    return state.GetDistributorNames?.GetDistributorNamesData;
  });
  const GetAgentsNames = useSelector((state) => {
    return state.GetAgentsNames?.GetAgentsNamesData;
  });
  const GetAllAgentsNames = useSelector((state) => {
    return state.GetAllAgentsNames?.GetAllAgentsNamesData;
  });
  const refreshCount = useSelector((state) => state.Refresh?.refreshCount);

  // Modal state for vehicle-class image popup
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedClass, setSelectedClass] = useState({});

  // State variables
  const [visibleRows, setVisibleRows] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inventoryData, setInventoryData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filter state variables
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(''); // Debounced search value
  const [status, setStatus] = useState(''); // Start with no status filter
  const [selectedVehicleClass, setSelectedVehicleClass] = useState('');
  const [selectedDistributor, setSelectedDistributor] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedDealer, setSelectedDealer] = useState('');
  const [activationFilter, setActivationFilter] = useState(''); // FASTag Activation status
  const [activationSortOrder, setActivationSortOrder] = useState('desc'); // 'desc' = newest first, 'asc' = oldest first
  const [dealerNames, setDealerNames] = useState([]);
  // Cascade agents (of the selected distributor/dealer). null = show all agents.
  const [cascadeAgents, setCascadeAgents] = useState(null);

  const [selectedTagActivated, setSelectedTagActivated] = useState(false);
  const [modalInitialTab, setModalInitialTab] = useState('cost');

  const isRowActivated = (item) => item?.isActivated === true || item?.userActivationStatus === 'Active';
  const [isDownloading, setIsDownloading] = useState(false);
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [undoFile, setUndoFile] = useState(null);
  const [isUndoing, setIsUndoing] = useState(false);
  const [selectedTagRefId, setSelectedTagRefId] = useState(null);
  const fileInputRef = useRef(null);

  // Filter data - ensure arrays and extract from API response structure
  const distributorDataNames = (() => {
    if (GetDistributorNames?.success && Array.isArray(GetDistributorNames?.data)) {
      return GetDistributorNames.data;
    } else if (Array.isArray(GetDistributorNames?.data)) {
      return GetDistributorNames.data;
    } else if (Array.isArray(GetDistributorNames)) {
      return GetDistributorNames;
    }
    return [];
  })();

  const agentDataNames = (() => {

    // Handle the exact API response format from your screenshot
    if (GetAllAgentsNames?.success === true && Array.isArray(GetAllAgentsNames?.data)) {
      return GetAllAgentsNames.data;
    }

    // Fallback for other possible formats
    if (Array.isArray(GetAllAgentsNames?.data)) {
      return GetAllAgentsNames.data;
    }

    if (Array.isArray(GetAllAgentsNames)) {
      return GetAllAgentsNames;
    }

    return [];
  })();

  const statusOptions = [
    { name: "All Status", value: "" },
    { name: "Unassigned", value: "Unassigned" },
    { name: "Assigned", value: "Assigned" },
  ];

  const toggleVisibility = (index, row) => {
    const isVisible = visibleRows[index];
    const newVisibility = { ...visibleRows, [index]: !isVisible };
    setVisibleRows(newVisibility);
    setModalOpen(!isVisible);

    if (!isVisible) {
      setSelectedClass(row);
      setSelectedIndex(index);
    }
  };

  // Cascade: Distributor -> Dealer -> Agent.
  // Selecting a distributor scopes the Dealer and Agent dropdowns to that
  // distributor; selecting a dealer scopes the Agent dropdown to that dealer.
  const handleDistributorChange = (distributorId) => {
    setSelectedDistributor(distributorId);
    setSelectedDealer('');   // reset cascade
    setSelectedAgent('');
    setCurrentPage(1);

    if (distributorId) {
      // Dealers of this distributor.
      API.GetAllDealersNames({ distributorId })
        .then((res) => setDealerNames(Array.isArray(res?.data) ? res.data : []))
        .catch(() => setDealerNames([]));
      // Agents of this distributor.
      API.GetAgentsNames({ distributorId })
        .then((res) => setCascadeAgents(Array.isArray(res?.data) ? res.data : []))
        .catch(() => setCascadeAgents([]));
    } else {
      // Back to "all" for both dropdowns.
      API.GetAllDealersNames()
        .then((res) => setDealerNames(Array.isArray(res?.data) ? res.data : []))
        .catch(() => setDealerNames([]));
      setCascadeAgents(null);
    }
  };

  const handleDealerChange = (dealerId) => {
    setSelectedDealer(dealerId);
    setSelectedAgent(''); // reset agent
    setCurrentPage(1);

    if (dealerId) {
      // Agents of the selected dealer (distributor stays as the parent scope).
      API.GetAgentsNames({ distributorId: selectedDistributor || '', dealerId })
        .then((res) => setCascadeAgents(Array.isArray(res?.data) ? res.data : []))
        .catch(() => setCascadeAgents([]));
    } else if (selectedDistributor) {
      // Fall back to the distributor's agents.
      API.GetAgentsNames({ distributorId: selectedDistributor })
        .then((res) => setCascadeAgents(Array.isArray(res?.data) ? res.data : []))
        .catch(() => setCascadeAgents([]));
    } else {
      setCascadeAgents(null);
    }
  };

  // Fetch data function — accepts explicit overrides so callers can pass
  // the very latest state value without relying on a closure snapshot.
  const fetchData = async ({
    page = currentPage,
    limit = itemsPerPage,
    search: searchParam = debouncedSearch,
    statusParam = status,
    VC = selectedVehicleClass,
    distributor = selectedDistributor,
    agent = selectedAgent,
    dealer = selectedDealer,
    isActivated = activationFilter,
    sortOrder = activationSortOrder,
    startDate: sd = selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : '',
    endDate: ed = selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : '',
  } = {}) => {
    setIsLoading(true);
    try {
      const params = {
        page,
        limit,
        ...(searchParam && { search: searchParam }),
        ...(statusParam && statusParam !== '' && { status: statusParam }),
        ...(VC && VC !== '' && { VC }),
        ...(distributor && distributor !== '' && { distributor }),
        ...(agent && agent !== '' && { agent }),
        ...(dealer && dealer !== '' && { dealer }),
        ...(isActivated && isActivated !== '' && { isActivated }),
        // Pass sort direction when Activated filter is on
        ...(isActivated === 'true' && { activationSortOrder: sortOrder }),
        ...(sd && { startDate: sd, endDate: ed }),
      };
      const response = await API.GetMasterFastag(params);
      if (response && response.data) {
        setInventoryData(Array.isArray(response.data) ? response.data : []);
        const pg = response.pagination || {};
        setTotalRecords(pg.total || 0);
        setTotalPages(pg.totalPages || Math.max(1, Math.ceil((pg.total || 0) / limit)));
      } else {
        setInventoryData([]);
        setTotalRecords(0);
        setTotalPages(1);
      }
    } catch (err) {
      setInventoryData([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  };

  // Debouncing effect for search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 1000); // 1 second delay

    return () => clearTimeout(timer);
  }, [search]);

  // Load data on component mount
  useEffect(() => {
    fetchData();
    // Fetch distributor names on component mount
    dispatch(ACTIONS.GetDistributorNamesAction());
    // Fetch all agent names on component mount (not distributor-specific)
    dispatch(ACTIONS.GetAllAgentsNamesAction());
    // Fetch all dealer names for the "All Dealers" filter.
    API.GetAllDealersNames()
      .then((res) => setDealerNames(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setDealerNames([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch data when pagination, date, or filters change.
  // Pass current values explicitly to avoid stale-closure issues.
  useEffect(() => {
    fetchData({
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearch,
      statusParam: status,
      VC: selectedVehicleClass,
      distributor: selectedDistributor,
      agent: selectedAgent,
      dealer: selectedDealer,
      isActivated: activationFilter,
      sortOrder: activationSortOrder,
      startDate: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : '',
      endDate: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, selectedDate, debouncedSearch, status, selectedVehicleClass, selectedDistributor, selectedAgent, selectedDealer, activationFilter, activationSortOrder, refreshCount]);


  // Handle download
  const handleDownload = async () => {
    if (isDownloading) return;
    if (!totalRecords) {
      ErrorToast('No records match the current filters');
      return;
    }
    setIsDownloading(true);
    try {
      // Exactly the filters fetchData sends, so the file matches the table.
      const params = {
        search: debouncedSearch,
        status: status,
        VC: selectedVehicleClass,
        distributor: selectedDistributor,
        agent: selectedAgent,
        dealer: selectedDealer,
        isActivated: activationFilter,
        activationSortOrder: activationSortOrder,
        startDate: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : '',
        endDate: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : ''
      };

      const blob = await API.ExportMasterInventory(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `master-fastag-inventory-${moment().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      SuccessToast('File downloaded successfully');
    } catch (error) {
      // ExportMasterInventory reports the server's own reason (empty filter
      // result, timeout, connection) — a blanket message would hide all of it.
      ErrorToast(error?.message || 'Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleUndoUpload = async () => {
    if (!undoFile) {
      ErrorToast("Please select a file first");
      return;
    }

    setIsUndoing(true);
    const formData = new FormData();
    formData.append("file", undoFile);

    try {
      const response = await API.UndoInventoryExcel(formData);
      if (response.success) {
        SuccessToast(response.message || "Undo process completed successfully");
        setShowUndoModal(false);
        setUndoFile(null);
        fetchData(); // Refresh the table
      } else {
        const displayError = typeof response.error === 'string'
          ? response.error
          : (response.error?.message || response.message || "Failed to process undo");
        ErrorToast(displayError);
      }
    } catch (error) {
      ErrorToast("An error occurred during file upload");
    } finally {
      setIsUndoing(false);
    }
  };


  const handleBoxClick = () => {
    fileInputRef.current.click();
  };

  // Status Badge component
  const StatusBadge = ({ status }) => {
    const base = "px-2 py-[2px] rounded-full text-xs font-medium flex items-center gap-1 justify-self-start";
    if (status === "Assigned")
      return <span className={`${base} bg-green-100 text-green-900`}><span className="h-2 w-2 bg-green-500 rounded-full" />Assigned</span>;
    if (status === "Unassigned")
      return <span className={`${base} bg-yellow-100 text-yellow-900`}><span className="h-2 w-2 bg-yellow-500 rounded-full" />Unassigned</span>;
    return null;
  };

  const StatusBadge1 = ({ status }) => {
    const base = "px-2 py-[2px] rounded-full text-xs font-medium flex items-center gap-1 justify-self-start";

    // Handle true boolean or "Active" string
    if (status === true || status === "Active") {
      return (
        <span className={`${base} bg-green-100 text-green-900`}>
          <span className="h-2 w-2 bg-green-500 rounded-full" />
          Active
        </span>
      );
    }

    // Default fallback for false, "Inactive", null, or undefined
    return (
      <span className={`${base} bg-yellow-100 text-yellow-900`}>
        <span className="h-2 w-2 bg-yellow-500 rounded-full" />
        Inactive
      </span>
    );
  };

  // No dummy data - using only real API data
  const vehicleData = getVehicleClassData(selectedClass.vehicleClass);

  // CommonTable pads cells px-5 py-4; with 16 columns that is a lot of dead
  // space, so this table opts into a tighter box.
  const CELL = '!px-2.5 !py-2';
  const HEAD = '!px-2.5 !py-2.5';

  // One tier per cell: who holds the tag, when it moved, and its status.
  const TierCell = ({ name, date, badge }) => {
    const missing = !name || name === "N/A";
    return (
      <div className="leading-tight">
        <div className={`max-w-[150px] break-words ${missing ? 'text-gray-400' : 'font-medium text-gray-800'}`}>
          {name || "N/A"}
        </div>
        {(date || badge) && (
          <div className="flex items-start gap-1.5 mt-1">
            {badge}
            {date && moment(date).isValid() && (
              // Date on its own line with the time beneath it, matching the
              // other inventory tables — and narrower than one long line.
              <span className="text-[11px] text-gray-400 leading-tight whitespace-nowrap">
                <span className="block">{moment(date).local().format("DD MMM YYYY")}</span>
                <span className="block">{moment(date).local().format("hh:mm A")}</span>
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  // Table columns definition
  const columns = [
    {
      label: "Tag Ref ID",
      key: "tagRefId",
      className: HEAD,
      tdClassName: CELL,
      render: (item) => {
        const tagVal = item.tagRefId || item.serialNumber || '';
        const activated = isRowActivated(item);
        return (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span>{tagVal}</span>
              {tagVal && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTagRefId(tagVal);
                    setSelectedTagActivated(activated);
                    setModalInitialTab('cost');
                  }}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-red-500 focus:outline-none"
                  title="View pricing details"
                >
                  <Info size={14} />
                </button>
              )}
            </div>
            {tagVal && activated && (
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedTagRefId(tagVal); setSelectedTagActivated(true); setModalInitialTab('transactions'); }}
                className="text-xs font-medium text-blue-600 hover:underline text-left w-fit"
              >
                View Transactions
              </button>
            )}
          </div>
        );
      }
    },
    { label: "Batch ID", key: "batchId", className: HEAD, tdClassName: CELL },
    {
      label: "Vehicle Class",
      key: "vehicleClass",
      className: HEAD,
      tdClassName: CELL,
      render: (item, idx) => {
        const vehicleClassData = getVehicleClassData(item.vehicleClass);
        return (
          <div className="flex gap-2 items-center">
            <img src={vehicleClassData?.tagimage} alt="Tag" className="w-[34px]" />
            <p>{vehicleClassData?.value}</p>
            <div
              className="pl-2 flex items-center cursor-pointer"
              onClick={() => toggleVisibility(idx, item)}
            >
              {visibleRows[idx] ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </div>
          </div>
        );
      },
    },
    { label: "Supplier", key: "supplier", className: HEAD, tdClassName: CELL },
    // Each tier used to occupy three columns (name / date / status) — twelve in
    // all, which is what drove the horizontal scroll. They are grouped into one
    // cell per tier instead: same three values, stacked. Nothing is hidden.
    //
    // Distributor tier: its own slot stays filled even after the tag moves down
    // to a dealer. Falls back to the old role check for pre-existing records.
    {
      label: "Distributor",
      className: HEAD,
      tdClassName: `!whitespace-normal ${CELL}`,
      render: (item) => {
        const name =
          item.assignedToDistributerID?.username ||
          (item.assignedTo?.role === 'distributor' ? item.assignedTo?.username : "N/A");
        const dated =
          (item.assignedToDistributerID || item.assignedTo?.role === 'distributor') && item.assignedDate
            ? item.assignedDate
            : null;
        return <TierCell name={name} date={dated} badge={<StatusBadge status={item.status} />} />;
      },
    },
    // Dealer tier: its own slot is set only when the tag actually sits with a
    // dealer, so no role sniffing is needed.
    {
      label: "Dealer",
      className: HEAD,
      tdClassName: `!whitespace-normal ${CELL}`,
      render: (item) => {
        const isDealer = !!(item.assignedToDealerID || item.assignedTo?.role === 'dealer');
        const name =
          item.assignedToDealerID?.username ||
          (item.assignedTo?.role === 'dealer' ? item.assignedTo?.username : "N/A");
        return (
          <TierCell
            name={name}
            date={isDealer && item.assignedDate ? item.assignedDate : null}
            badge={isDealer ? <StatusBadge status={item.status} /> : null}
          />
        );
      },
    },
    {
      label: "Agent",
      className: HEAD,
      tdClassName: `!whitespace-normal ${CELL}`,
      render: (item) => (
        <TierCell
          name={item.agentAssignedTo?.username || "N/A"}
          date={item.agentAssignedDate || null}
          badge={<StatusBadge status={item.agentStatus} />}
        />
      ),
    },
    {
      label: "User",
      className: HEAD,
      tdClassName: `!whitespace-normal ${CELL}`,
      render: (item) => (
        <TierCell
          name={item?.userMobileNumber || "N/A"}
          date={null}
          badge={<StatusBadge1 status={item?.EndStatusActivation} />}
        />
      ),
    },
    ...(activationFilter === 'true' ? [{
      label: (
        <div className="flex items-center gap-1">
          <span>Activation Date</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setActivationSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
              setCurrentPage(1);
            }}
            title={activationSortOrder === 'desc' ? 'Newest first — click for oldest first' : 'Oldest first — click for newest first'}
            className="ml-0.5 text-[#EE3B31] hover:text-red-700 transition-colors focus:outline-none"
          >
            {activationSortOrder === 'desc' ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronUp className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      ),
      className: HEAD,
      tdClassName: `!whitespace-normal ${CELL}`,
      render: (item) => {
        const date = item.userActivationDate;
        if (!date || !moment(date).isValid()) {
          return <span className="text-gray-400 text-xs">—</span>;
        }
        const m = moment(date).local();
        return (
          <div className="leading-tight">
            <div className="text-[12px] font-medium text-gray-800 whitespace-nowrap">{m.format('DD MMM YYYY')}</div>
            <div className="text-[11px] text-gray-400 whitespace-nowrap">{m.format('hh:mm A')}</div>
          </div>
        );
      },
    }] : []),
    // {
    //   label: "Created Date",
    //   key: "createdAt",
    //   render: (item) => moment(item.createdAt).local().format("DD MMMM, YYYY, hh:mm A")
    // },
  ];

  // Use only real API data
  const displayData = inventoryData || [];
  const displayTotal = totalRecords || 0;
  const displayTotalPages = totalPages || 1;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 mt-2">
        {showBackButton && (
          <button
            className="back-arrow leading-none"
            style={{ fontSize: '20px', color: '#ff3f34', cursor: 'pointer', fontWeight: 'bold' }}
            onClick={() => navigate(-1)}
          >
            ←
          </button>
        )}
        <h1 className="text-xl font-bold text-gray-800 leading-tight">Master FASTag Inventory</h1>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow-sm px-4 py-3 mb-3">
        {/* Filters fill the row; the actions sit at its right edge rather than
            taking a full-width row of their own underneath. */}
        <div className="flex flex-col xl:flex-row xl:items-end gap-2.5">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-2.5 flex-1 min-w-0">
            {/* Search */}
            <div className="relative">
              <TextInput
                name="search"
                placeholder="TagRefId / Batch ID"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                inputClass="pl-3 pr-9 py-2 text-sm"
              />
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                <Search className="w-4 h-4" />
              </div>
            </div>

            {/* Status Filter */}
            <CustomSelectInput
              name="status"
              label=""
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setCurrentPage(1);
              }}
              options={statusOptions}
              inputClass="py-2 px-3 text-sm"
            />

            {/* Vehicle Class Filter */}
            <CustomSelectInput
              name="vehicleClass"
              label=""
              value={selectedVehicleClass}
              onChange={(e) => {
                setSelectedVehicleClass(e.target.value);
                setCurrentPage(1);
              }}
              options={[
                { name: "All Vehicle Classes", value: "" },
                ...vehicleClasses.map((vehicle) => ({
                  ...vehicle,
                  name: vehicle.value,
                  value: vehicle.value
                })),
              ]}
              inputClass="py-2 px-3 text-sm"
            />

            {/* FASTag Activation Status Filter */}
            <CustomSelectInput
              name="activationFilter"
              label=""
              value={activationFilter}
              onChange={(e) => { setActivationFilter(e.target.value); setCurrentPage(1); }}
              options={[
                { name: "Select Status", value: "" },
                { name: "Activated", value: "true" },
                { name: "Available", value: "false" },
              ]}
              inputClass="py-2 px-3 text-sm"
            />

            {/* Distributor Filter */}
            <CustomSelectInput
              name="distributor"
              label=""
              value={selectedDistributor}
              onChange={(e) => {
                handleDistributorChange(e.target.value);
              }}
              options={[
                { name: "All Distributors", value: "" },
                ...distributorDataNames.map((distributor, index) => {
                  return {
                    name: distributor?.username || `Distributor ${index + 1}`,
                    value: distributor?._id || distributor?.id || ''
                  };
                }),
              ]}
              showSearch={true}
              inputClass="py-2 px-3 text-sm"
            />

            {/* Dealer Filter */}
            <CustomSelectInput
              name="dealer"
              label=""
              value={selectedDealer}
              onChange={(e) => {
                handleDealerChange(e.target.value);
              }}
              options={[
                { name: "All Dealers", value: "" },
                ...dealerNames.map((dealer, index) => ({
                  name: dealer?.username || `Dealer ${index + 1}`,
                  value: dealer?._id || dealer?.id || ''
                })),
              ]}
              showSearch={true}
              inputClass="py-2 px-3 text-sm"
            />

            {/* Agent Filter */}
            <CustomSelectInput
              name="agent"
              label=""
              value={selectedAgent}
              onChange={(e) => {
                setSelectedAgent(e.target.value);
                setCurrentPage(1);
              }}
              options={[
                { name: "All Agents", value: "" },
                ...(cascadeAgents !== null ? cascadeAgents : agentDataNames).map((agent, index) => {
                  return {
                    name: agent?.username || `Agent ${index + 1}`,
                    value: agent?._id || agent?.id || ''
                  };
                }),
              ]}
              showSearch={true}
              inputClass="py-2 px-3 text-sm"
            />
          </div>

          {/* Actions — right edge of the same row as the filters */}
          <div className="flex items-center justify-end gap-2 shrink-0">
            <button
              onClick={() => {
                setSearch('');
                setDebouncedSearch(''); // clear now, don't wait out the debounce
                setStatus('');
                setSelectedVehicleClass('');
                setSelectedDistributor('');
                setSelectedAgent('');
                setSelectedDealer('');
                setActivationFilter('');
                setActivationSortOrder('desc'); // reset sort direction
                setSelectedDate(null);
                setCascadeAgents(null); // back to all agents
                // Reset the dealer dropdown to all dealers.
                API.GetAllDealersNames()
                  .then((res) => setDealerNames(Array.isArray(res?.data) ? res.data : []))
                  .catch(() => setDealerNames([]));
                setCurrentPage(1);
                setItemsPerPage(10); // pager back to page 1 at the default size
              }}
              title="Reset Filters"
              aria-label="Reset Filters"
              className="w-full xl:w-[38px] h-[38px] border border-[#EE3B31] text-[#EE3B31] rounded-lg font-semibold transition-all duration-200 hover:bg-[#EE3B31] hover:text-white flex items-center justify-center shadow-sm hover:shadow cursor-pointer"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowUndoModal(true)}
              className="bg-red-600 text-white py-1.5 px-3 text-sm rounded-md font-medium transition-colors"
            >
              Reverse Inventory
            </button>
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
          <div className="relative bg-white rounded-[20px] shadow-lg w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 md:p-6">
              <div className="absolute top-4 right-4 gap-3 flex-row flex">
                <button
                  onClick={() => {
                    setModalOpen(false);
                    if (selectedIndex !== null) {
                      toggleVisibility(selectedIndex, selectedClass);
                    }
                  }}
                  className="text-gray-600 hover:text-red-500 p-1"
                >
                  <X size={18} className="md:w-5 md:h-5" />
                </button>
              </div>
              <h2 className="text-lg md:text-xl font-semibold mb-[4px] text-center pr-16">
                {vehicleData?.value}
              </h2>
              <p className="mb-[20px] md:mb-[30px] text-xs md:text-sm text-center px-2">
                {vehicleData?.description}
              </p>
              <div className="relative w-full flex justify-center items-center py-8 px-4">
                <div className="relative bg-white p-8 rounded-2xl shadow-2xl border border-gray-200 max-w-2xl w-full">
                  {/* Decorative corner accents */}
                  <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-red-500 rounded-tl-2xl opacity-20"></div>
                  <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-red-500 rounded-br-2xl opacity-20"></div>

                  {/* Image container with classic frame */}
                  <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl border-2 border-gray-300 shadow-inner">
                    <img
                      src={vehicleData?.cardWebp}
                      alt={vehicleData?.name}
                      className="w-full h-auto object-contain rounded-lg transition-transform duration-500 hover:scale-[1.12]"
                    />
                  </div>

                  {/* Elegant bottom accent */}
                  <div className="mt-6 flex justify-center">
                    <div className="h-1 w-24 bg-gradient-to-r from-transparent via-red-500 to-transparent rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <CommonTable
        loading={isLoading}
        isPagination={true}
        paginationClass="!mt-3"
        columns={columns}
        data={displayData}
        emptyMessage="No inventory data found"
        total={displayTotal}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        totalPages={displayTotalPages}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        extraTableClass="bg-white rounded-b-2xl shadow-sm"
        hideDownload={false}
        onDownload={handleDownload}
        downloading={isDownloading}
      />      {/* Reverse Inventory Modal — file upload + manual selection (shared component) */}
      <ReverseInventoryModal
        open={showUndoModal}
        role="admin"
        onClose={() => { setShowUndoModal(false); setUndoFile(null); }}
        onSuccess={() => fetchData()}
      />
      {selectedTagRefId && (
        <TagCostInfoModal
          tagRefId={selectedTagRefId}
          isActivated={selectedTagActivated}
          initialTab={modalInitialTab}
          onClose={() => { setSelectedTagRefId(null); setSelectedTagActivated(false); }}
        />
      )}
    </div>
  );
};

export default MasterFASTag;
